/**
 * Spec 088a (gap 088a-1) — admin UI for feature flags.
 *
 * Card-per-flag layout with friendly names, plain-English descriptions,
 * cost-impact pills, dependency chains, and tick cadence. Toggling
 * PATCHes /api/v1/admin/feature-flags/:key with the flipped value,
 * applies an optimistic update, and reverts on error.
 *
 * Role gate: `orbit:admin` ONLY. The other admin pages accept either
 * `rule_admin` or `orbit:admin`, but flipping worker flags is a higher-
 * privilege action (see spec 088a FR-F6).
 *
 * The 15-second server-side cache (`src/lib/feature-flags.ts`) means a
 * change made here propagates to all app processes within ~15s.
 */
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../components/ui/Icon.js';
import {
  listFeatureFlags,
  patchFeatureFlag,
  type FlagRow,
} from '../../api/featureFlags.js';
import { useToast } from '../../hooks/useToast.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { getFlagMeta, costPillStyle } from '../../lib/feature-flag-meta.js';
import { RowList, Row } from '../../components/ui/Row.js';
import { Chip } from '../../components/ui/Chip.js';

export function FeatureFlagsPage() {
  const toast = useToast();
  const [rows, setRows] = useState<FlagRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listFeatureFlags()
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Quick lookup of "is flag X enabled?" for dependency checks.
  const enabledMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const r of rows) m.set(r.key, r.enabled);
    return m;
  }, [rows]);

  async function onToggle(flag: FlagRow) {
    const nextEnabled = !flag.enabled;
    const prevRows = rows;
    // Optimistic update
    setRows((current) =>
      current.map((r) => (r.key === flag.key ? { ...r, enabled: nextEnabled } : r)),
    );
    setPendingKey(flag.key);
    try {
      const updated = await patchFeatureFlag(flag.key, nextEnabled);
      setRows((current) =>
        current.map((r) => (r.key === flag.key ? updated : r)),
      );
      toast.addToast(
        `${flag.key} ${nextEnabled ? 'enabled' : 'disabled'}`,
        'success',
      );
    } catch (e) {
      // Revert optimistic state on failure.
      setRows(prevRows);
      toast.addToast(
        e instanceof Error ? e.message : 'Toggle failed',
        'error',
      );
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <div data-testid="admin-feature-flags" className="hub-page--table">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Feature flags' },
        ]}
      />
      <header>
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-toggle-right" className="text-violet-600" />
          Feature flags
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Toggle background workers and client-facing features. Changes
          propagate to all app processes within 15 seconds. Each flag below
          shows what happens when enabled, its cost impact, and which other
          flags it depends on.
        </p>
      </header>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {err}
        </div>
      )}

      {loading && (
        <div className="hub-plate hub-plate--flat text-center text-sm text-slate-500">
          Loading flags…
        </div>
      )}

      {!loading && rows.length === 0 && !err && (
        <div className="hub-plate hub-plate--flat text-center text-sm text-slate-500">
          No feature flags defined.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <RowList data-testid="feature-flags-list">
          {rows.map((flag) => (
            <FlagCard
              key={flag.key}
              flag={flag}
              enabledMap={enabledMap}
              pending={pendingKey === flag.key}
              onToggle={() => onToggle(flag)}
            />
          ))}
        </RowList>
      )}
    </div>
  );
}

interface FlagCardProps {
  flag: FlagRow;
  enabledMap: Map<string, boolean>;
  pending: boolean;
  onToggle: () => void;
}

function FlagCard({ flag, enabledMap, pending, onToggle }: FlagCardProps) {
  const meta = getFlagMeta(flag.key);
  const costPill = costPillStyle(meta.cost);

  // Dependency violation: this flag is on but a required upstream flag is off.
  const missingDeps = flag.enabled
    ? meta.dependsOn.filter((d) => enabledMap.get(d) === false)
    : [];

  return (
    // Action-only row: no detail page for a flag, and its one interactive
    // affordance is the switch — disclosure shape, no `to`. The old
    // conditional border colour (emerald when enabled, slate when not) is
    // dropped without information loss, unlike DiagnosisCard's severity
    // accent: the switch itself already carries on/off both visually
    // (its thumb position) and accessibly (aria-checked), so nothing here
    // was the ONLY carrier of that state. The Requires/Unlocks sm:grid-cols-2
    // block below is inner layout and stays untouched.
    <Row
      data-testid={`flag-row-${flag.key}`}
      title={meta.friendlyName}
      top={
        <>
          <Chip tone={costPill.tone} title={meta.costExplain}>
            <Icon name="bx-dollar-circle" /> {costPill.label}
          </Chip>
          <Chip title="When the work fires">
            <Icon name="bx-time-five" /> {meta.tickCadence}
          </Chip>
          <span className="font-mono text-xs text-slate-400">{flag.key}</span>
        </>
      }
      actions={
        <FlagSwitch
          enabled={flag.enabled}
          pending={pending}
          flagKey={flag.key}
          onToggle={onToggle}
        />
      }
    >
      {/* Dependency-violation banner */}
      {missingDeps.length > 0 && (
        <div
          data-testid={`flag-dep-warn-${flag.key}`}
          className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          <Icon name="bx-error" className="mt-0.5 flex-shrink-0" />
          <div>
            <strong>Won&apos;t do useful work yet —</strong> this flag is on but
            its upstream is off:
            <ul className="mt-1 list-disc pl-4">
              {missingDeps.map((d) => (
                <li key={d}>
                  <span className="font-mono">{getFlagMeta(d).friendlyName}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Description */}
      <p className="mt-3 text-sm text-slate-700">{meta.description}</p>

      {/* When enabled */}
      {meta.whenEnabled.length > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-xs font-semibold text-slate-500 select-none hover:text-slate-700">
            What happens when enabled
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
            {meta.whenEnabled.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </details>
      )}

      {/* Dependencies + Enables */}
      {(meta.dependsOn.length > 0 || meta.enables.length > 0) && (
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {meta.dependsOn.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500">
                Requires
              </div>
              <ul className="mt-1 space-y-1">
                {meta.dependsOn.map((dep) => {
                  const depEnabled = enabledMap.get(dep) ?? false;
                  return (
                    <li key={dep} className="flex items-center gap-1.5 text-xs">
                      {depEnabled ? (
                        <Icon name="bx-check-circle" className="flex-shrink-0 text-emerald-600" />
                      ) : (
                        <Icon name="bx-x-circle" className="flex-shrink-0 text-rose-500" />
                      )}
                      <span
                        className={
                          depEnabled ? 'text-slate-700' : 'text-rose-700'
                        }
                      >
                        {getFlagMeta(dep).friendlyName}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {meta.enables.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-500">
                Unlocks
              </div>
              <ul className="mt-1 space-y-1">
                {meta.enables.map((next) => (
                  <li key={next} className="flex items-center gap-1.5 text-xs text-slate-700">
                    <Icon name="bx-right-arrow-alt" className="flex-shrink-0 text-violet-500" />
                    {getFlagMeta(next).friendlyName}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Footer: provenance. The backend `notes` column intentionally
          omitted — it's an internal spec-number reference that adds no
          user value. */}
      <footer className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500">
        Last toggled {flag.updated_by ?? 'system'} ·{' '}
        {new Date(flag.updated_at).toLocaleString()}
      </footer>
    </Row>
  );
}

interface FlagSwitchProps {
  enabled: boolean;
  pending: boolean;
  flagKey: string;
  onToggle: () => void;
}

function FlagSwitch({ enabled, pending, flagKey, onToggle }: FlagSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={`Toggle feature flag ${flagKey}`}
      data-testid={`flag-toggle-${flagKey}`}
      onClick={onToggle}
      disabled={pending}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 ${
        enabled ? 'bg-emerald-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white  transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
